/**
 * AgentMeter SDK Main Client
 * Main client class providing all AgentMeter API functionality
 */

import { HttpClient } from './utils/http';
import { validateApiKey, validatePaginationParams } from './utils/validation';
import {
  validateCreateAppRequest,
  validateUpdateAppRequest,
  validateCreateMeterRequest,
  validateUpdateMeterRequest,
  validateCreateEventRequest,
} from './utils/validation';

import type {
  AgentMeterConfig,
  ApiResponse,
  App,
  Meter,
  Event,
  Usage,
  UsageSummary,
  UsageTimeSeries,
  CreateAppRequest,
  UpdateAppRequest,
  CreateMeterRequest,
  UpdateMeterRequest,
  CreateEventRequest,
  EventsQueryParams,
  UsageQueryParams,
  UsageTimeSeriesParams,
} from './types';

/**
 * Default configuration values
 */
const DEFAULT_CONFIG = {
  baseUrl: 'https://tcwvgqbyyboxnbrsbchx.functions.supabase.co',
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
  debug: false,
};

/**
 * Main AgentMeter SDK client
 */
export class AgentMeterClient {
  private httpClient: HttpClient;
  private config: Required<AgentMeterConfig>;

  /**
   * Create a new AgentMeter client instance
   * 
   * @param config - Configuration object containing API key and optional settings
   */
  constructor(config: AgentMeterConfig) {
    validateApiKey(config.apiKey);

    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };

    this.httpClient = new HttpClient({
      baseUrl: this.config.baseUrl,
      apiKey: this.config.apiKey,
      timeout: this.config.timeout,
      retryAttempts: this.config.retryAttempts,
      retryDelay: this.config.retryDelay,
      debug: this.config.debug,
    });
  }

  // ===================
  // Apps API Methods
  // ===================

  /**
   * Create a new application
   */
  async createApp(request: CreateAppRequest): Promise<ApiResponse<App>> {
    validateCreateAppRequest(request);
    const response = await this.httpClient.post<ApiResponse<App>>('/apps', request);
    return response.data;
  }

  /**
   * Get all applications
   */
  async getApps(): Promise<ApiResponse<App[]>> {
    const response = await this.httpClient.get<ApiResponse<App[]>>('/apps');
    return response.data;
  }

  /**
   * Get a specific application by ID
   */
  async getApp(id: string): Promise<ApiResponse<App>> {
    if (!id || typeof id !== 'string') {
      throw new Error('App ID is required and must be a string');
    }

    const response = await this.httpClient.get<ApiResponse<App>>(`/apps/${id}`);
    return response.data;
  }

  /**
   * Update an existing application
   */
  async updateApp(id: string, request: UpdateAppRequest): Promise<ApiResponse<App>> {
    if (!id || typeof id !== 'string') {
      throw new Error('App ID is required and must be a string');
    }

    validateUpdateAppRequest(request);
    const response = await this.httpClient.put<ApiResponse<App>>(`/apps/${id}`, request);
    return response.data;
  }

  /**
   * Delete an application
   */
  async deleteApp(id: string): Promise<ApiResponse<void>> {
    if (!id || typeof id !== 'string') {
      throw new Error('App ID is required and must be a string');
    }

    const response = await this.httpClient.delete<ApiResponse<void>>(`/apps/${id}`);
    return response.data;
  }

  // ===================
  // Meters API Methods
  // ===================

  /**
   * Create a new meter
   */
  async createMeter(request: CreateMeterRequest): Promise<ApiResponse<Meter>> {
    validateCreateMeterRequest(request);
    const response = await this.httpClient.post<ApiResponse<Meter>>('/meters', request);
    return response.data;
  }

  /**
   * Get meters for a specific app
   */
  async getMeters(appId: string): Promise<ApiResponse<Meter[]>> {
    if (!appId || typeof appId !== 'string') {
      throw new Error('App ID is required and must be a string');
    }

    const response = await this.httpClient.get<ApiResponse<Meter[]>>('/meters', { app_id: appId });
    return response.data;
  }

  /**
   * Get a specific meter by ID with statistics
   */
  async getMeter(id: string): Promise<ApiResponse<Meter>> {
    if (!id || typeof id !== 'string') {
      throw new Error('Meter ID is required and must be a string');
    }

    const response = await this.httpClient.get<ApiResponse<Meter>>(`/meters/${id}`);
    return response.data;
  }

  /**
   * Update an existing meter
   */
  async updateMeter(id: string, request: UpdateMeterRequest): Promise<ApiResponse<Meter>> {
    if (!id || typeof id !== 'string') {
      throw new Error('Meter ID is required and must be a string');
    }

    validateUpdateMeterRequest(request);
    const response = await this.httpClient.put<ApiResponse<Meter>>(`/meters/${id}`, request);
    return response.data;
  }

  /**
   * Delete a meter
   */
  async deleteMeter(id: string): Promise<ApiResponse<void>> {
    if (!id || typeof id !== 'string') {
      throw new Error('Meter ID is required and must be a string');
    }

    const response = await this.httpClient.delete<ApiResponse<void>>(`/meters/${id}`);
    return response.data;
  }

  // ===================
  // Events API Methods
  // ===================

  /**
   * Track a single event
   */
  async trackEvent(request: CreateEventRequest): Promise<ApiResponse<Event>> {
    validateCreateEventRequest(request);
    const response = await this.httpClient.post<ApiResponse<Event>>('/events', request);
    return response.data;
  }

  /**
   * Track multiple events in batch
   */
  async trackEvents(events: CreateEventRequest[]): Promise<ApiResponse<Event[]>> {
    if (!Array.isArray(events) || events.length === 0) {
      throw new Error('Events array is required and must not be empty');
    }

    // Validate each event
    events.forEach((event, index) => {
      try {
        validateCreateEventRequest(event);
      } catch (error) {
        throw new Error(`Event at index ${index} is invalid: ${(error as Error).message}`);
      }
    });

    const response = await this.httpClient.post<ApiResponse<Event[]>>('/events/batch', { events });
    return response.data;
  }

  /**
   * Query events with filters
   */
  async getEvents(params: EventsQueryParams = {}): Promise<ApiResponse<Event[]>> {
    validatePaginationParams(params.page, params.limit);

    const response = await this.httpClient.get<ApiResponse<Event[]>>('/events', params);
    return response.data;
  }

  // ===================
  // Usage API Methods
  // ===================

  /**
   * Get usage summary for billing
   */
  async getUsage(params: UsageQueryParams): Promise<ApiResponse<UsageSummary>> {
    if (!params.app_id || typeof params.app_id !== 'string') {
      throw new Error('app_id is required and must be a string');
    }

    const response = await this.httpClient.get<ApiResponse<UsageSummary>>('/usage', params);
    return response.data;
  }

  /**
   * Get usage data over time for analytics
   */
  async getUsageTimeSeries(params: UsageTimeSeriesParams): Promise<ApiResponse<UsageTimeSeries>> {
    if (!params.meter_id || typeof params.meter_id !== 'string') {
      throw new Error('meter_id is required and must be a string');
    }

    if (params.interval && !['hour', 'day', 'week', 'month'].includes(params.interval)) {
      throw new Error('interval must be one of: hour, day, week, month');
    }

    const response = await this.httpClient.get<ApiResponse<UsageTimeSeries>>('/usage/timeseries', params);
    return response.data;
  }

  // ===================
  // Convenience Methods
  // ===================

  /**
   * Quick method to track API calls with user info
   */
  async trackApiCall(
    userId: string,
    endpoint: string,
    method: string = 'GET',
    properties?: Record<string, any>
  ): Promise<ApiResponse<Event>> {
    return this.trackEvent({
      event_name: 'api_call',
      user_id: userId,
      properties: {
        endpoint,
        method,
        ...properties,
      },
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Quick method to track feature usage
   */
  async trackFeatureUsage(
    userId: string,
    featureName: string,
    value: number = 1,
    properties?: Record<string, any>
  ): Promise<ApiResponse<Event>> {
    return this.trackEvent({
      event_name: 'feature_usage',
      user_id: userId,
      value,
      properties: {
        feature: featureName,
        ...properties,
      },
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Get billing summary for a specific period
   */
  async getBillingSummary(
    appId: string,
    periodStart: string,
    periodEnd: string,
    groupByUser: boolean = false
  ): Promise<ApiResponse<UsageSummary>> {
    return this.getUsage({
      app_id: appId,
      period_start: periodStart,
      period_end: periodEnd,
      group_by: groupByUser ? 'user_id' : undefined,
    });
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<Required<AgentMeterConfig>> {
    return { ...this.config };
  }

  /**
   * Update API key
   */
  updateApiKey(newApiKey: string): void {
    validateApiKey(newApiKey);
    this.config.apiKey = newApiKey;
    this.httpClient = new HttpClient({
      baseUrl: this.config.baseUrl,
      apiKey: this.config.apiKey,
      timeout: this.config.timeout,
      retryAttempts: this.config.retryAttempts,
      retryDelay: this.config.retryDelay,
      debug: this.config.debug,
    });
  }
}